import { getEnvByHostname } from '../utils/env';
import React, { useState, useEffect } from 'react';
import { Card, Form, Input, Select, Button, Space, message, Row, Col, Table, Tag, Alert, Divider } from 'antd';
import { FundOutlined, ReloadOutlined, SearchOutlined } from '@ant-design/icons';
import ReactJson from '@microlink/react-json-view';
import { buildChannelOptions, getCurrencyIcon } from '../utils/channelLogos';
import { testApi } from '../services/api';
import { buildBalanceRequestBody, parseBalanceResult } from '../utils/channelBalance';
import { useLanguage } from '../contexts/LanguageContext';
import { usePageCache } from '../contexts/PageCacheContext';

/**
 * 渠道余额查询（通用页面，2026-10 随 FINCRA 接入金丝雀新增）。
 *
 * 为什么要有：渠道接入时要核对「渠道钱包真实余额」与我方渠道余额记账是否一致（如 FINCRA 提现手续费另收，
 * 钱包实际扣「金额 + 手续费」）。此前金丝雀没有余额入口，只能找开发 curl 中台接口。
 *
 * 链路与权限：testType=BALANCE_QUERY → test-platform 签名后 POST /v1/fiat/payment/channel/balance/query。
 * test-platform 把它当只读查询：只校验用户 / 渠道 / 环境权限，不占每日次数；渠道以外层 channelCode 为准，
 * 跨业务线参数会被剥离（见 test-platform FiatPaymentClient#normalizeBalanceQueryBody）。
 * 页面对所有实现了 queryChannelBalance 的渠道通用，不写渠道特例；渠道下拉只列当前用户有权限的渠道。
 */
export default function ChannelBalance() {
  const [form] = Form.useForm();
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  // result：test-platform 原始执行结果（右侧 JSON 展示）；parsed：parseBalanceResult 的结论（表格 / 提示）
  const [result, setResult] = useState(null);
  const [parsed, setParsed] = useState(null);
  const [userChannels, setUserChannels] = useState([]);
  const { cache, save: saveCache } = usePageCache('channelBalance');
  const selectedChannel = Form.useWatch('channelCode', form);

  useEffect(() => {
    loadUserChannels();
    // 与其它页面一致：切走再切回时恢复上次的查询条件与结果
    if (cache) {
      if (cache.result) {
        setResult(cache.result);
        setParsed(parseBalanceResult(cache.result));
      }
      if (cache.formValues) form.setFieldsValue(cache.formValues);
    }
    // 只在挂载时执行一次（与其它页面的初始化方式一致）
  }, []);

  useEffect(() => {
    return () => {
      saveCache({ result, formValues: form.getFieldsValue() });
    };
  });

  const loadUserChannels = async () => {
    try {
      const { data } = await testApi.getChannelCurrencies();
      const map = data.data?.responseBody || data.data || {};
      const channels = Object.keys(map).map(channelCode => ({
        channelCode,
        currencies: [...new Set(map[channelCode] || [])],
      }));
      setUserChannels(channels);
      // 没有缓存的选择时默认选第一个渠道；币种默认不选 = 查询该渠道全部开放币种
      if (channels.length > 0 && !form.getFieldValue('channelCode')) {
        form.setFieldsValue({ channelCode: channels[0].channelCode });
      }
    } catch (e) {
      setUserChannels([]);
    }
  };

  const currencyOptions = (userChannels.find(c => c.channelCode === selectedChannel)?.currencies || [])
    .map(c => ({ label: `${getCurrencyIcon(c)} ${c}`, value: c }));

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const body = buildBalanceRequestBody(values);
      const response = await testApi.executeTest({
        testType: 'BALANCE_QUERY',
        channelCode: values.channelCode,
        businessPartner: 'DEMO_ACCOUNT',
        currency: body.currency || '',
        env: getEnvByHostname(),
        executor: localStorage.getItem('username'),
        requestBody: JSON.stringify(body),
      });
      const res = response.data;
      const conclusion = parseBalanceResult(res);
      // 右侧 JSON 里把 responseBody 展开成对象便于阅读（parseBalanceResult 两种形态都能处理，缓存恢复时不受影响）
      if (res?.data?.responseBody && typeof res.data.responseBody === 'string') {
        try { res.data.responseBody = JSON.parse(res.data.responseBody); } catch (e) { /* 非 JSON 原样展示 */ }
      }
      setResult(res);
      setParsed(conclusion);
      if (!conclusion.ok) {
        message.warning(`${t('balanceQueryFailed')}: ${conclusion.message || t('failed')}`);
      }
    } catch (error) {
      const msg = error.response?.data?.msg || error.message;
      message.error(`${t('balanceQueryFailed')}: ${msg}`);
      setResult(error.response?.data || { error: error.message });
      setParsed({ ok: false, rows: [], message: msg });
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    form.resetFields();
    setResult(null);
    setParsed(null);
    if (userChannels.length > 0) form.setFieldsValue({ channelCode: userChannels[0].channelCode });
  };

  const columns = [
    { title: t('channel'), dataIndex: 'channelCode', key: 'channelCode' },
    { title: t('currency'), dataIndex: 'currency', key: 'currency', render: c => (c ? `${getCurrencyIcon(c)} ${c}` : '-') },
    {
      title: t('availableBalance'), dataIndex: 'availableBalance', key: 'availableBalance', align: 'right',
      render: v => <span style={{ fontWeight: 600, color: '#10b981' }}>{v ?? '-'}</span>,
    },
    {
      // 渠道有多种余额概念时才返回，如 FINCRA 的 LEDGER（账面余额）
      title: t('balanceDetails'), dataIndex: 'balanceDetails', key: 'balanceDetails',
      render: details => (details?.length
        ? details.map((d, i) => <Tag key={`${d.type}-${i}`} style={{ marginBottom: 2 }}>{d.type}: {d.amount}</Tag>)
        : '-'),
    },
    { title: t('balanceChannelAccountNo'), dataIndex: 'channelAccountNo', key: 'channelAccountNo', render: v => (v ? <code>{v}</code> : '-') },
  ];

  const renderConclusion = () => {
    if (!parsed) return null;
    if (!parsed.ok) {
      return <Alert type="error" showIcon message={t('balanceQueryFailed')} description={parsed.message || undefined} />;
    }
    if (parsed.rows.length === 0) {
      return <Alert type="info" showIcon message={t('balanceEmpty')} />;
    }
    return <Table size="small" columns={columns} dataSource={parsed.rows} pagination={false} scroll={{ x: true }} />;
  };

  return (
    <div style={{ padding: '16px 24px', height: 'calc(100vh - 64px)', overflow: 'auto' }}>
      <div className="fade-in-up">
        <div style={{
          background: 'linear-gradient(135deg, #0ea5e9 0%, #2563eb 100%)', padding: '12px 24px', borderRadius: '8px',
          marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          boxShadow: '0 4px 12px rgba(37, 99, 235, 0.3)',
        }}>
          <Space size={12}>
            <FundOutlined style={{ color: '#fff', fontSize: 20 }} />
            <span style={{ fontSize: 18, fontWeight: 600, color: '#fff' }}>{t('channelBalance')}</span>
          </Space>
          <Button icon={<ReloadOutlined />} onClick={handleReset} type="text" style={{ color: '#fff' }}>{t('reset')}</Button>
        </div>

        <Row gutter={16}>
          <Col xs={24} lg={9}>
            <Card className="modern-card" bodyStyle={{ padding: '24px' }}>
              <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 16, color: '#374151' }}>⚙️ {t('balanceQueryParams')}</div>
              <Form form={form} layout="vertical" onFinish={handleSubmit}>
                <Form.Item label={t('channel')} name="channelCode" rules={[{ required: true }]}>
                  <Select
                    placeholder={t('selectChannel')}
                    options={buildChannelOptions(userChannels.map(c => c.channelCode))}
                    // 换渠道后旧币种可能不属于新渠道，清掉，回到「全部币种」
                    onChange={() => form.setFieldsValue({ currency: undefined })}
                  />
                </Form.Item>
                <Form.Item label={t('currency')} name="currency" tooltip={t('balanceCurrencyTip')}>
                  <Select allowClear placeholder={t('balanceCurrencyAll')} options={currencyOptions} />
                </Form.Item>
                <Form.Item label={t('balanceChannelAccountNo')} name="channelAccountNo" tooltip={t('balanceChannelAccountNoTip')}>
                  <Input allowClear placeholder={t('balanceChannelAccountNo')} />
                </Form.Item>
                <Button type="primary" htmlType="submit" icon={<SearchOutlined />} loading={loading} size="large" block
                  className="primary-button" style={{ height: 48, marginTop: 8 }}>
                  {t('queryBalance')}
                </Button>
              </Form>
            </Card>
          </Col>
          <Col xs={24} lg={15}>
            <Card className="modern-card" title={`📋 ${t('balanceResult')}`} bodyStyle={{ padding: '24px' }}>
              {parsed ? (
                <>
                  {renderConclusion()}
                  <Divider style={{ margin: '16px 0' }}>{t('response')}</Divider>
                  <div className="json-viewer">
                    <ReactJson src={result || {}} collapsed={2} theme="rjv-default" displayDataTypes={false}
                      displayObjectSize={false} enableClipboard={true} />
                  </div>
                </>
              ) : (
                <div style={{ padding: 60, textAlign: 'center', color: '#999', background: '#f9fafb', borderRadius: 8, border: '2px dashed #d1d5db' }}>
                  <FundOutlined style={{ fontSize: 48, marginBottom: 16, color: '#d1d5db' }} />
                  <div>{t('submitToShowBalanceResult')}</div>
                </div>
              )}
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  );
}
